import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vereinsAnmeldungSpeichern } from '../../lib/anmeldungSpeichern';

export default function LoginModal({ isOpen, onClose, onLogin, selectedItem = null }) {
  const navigate = useNavigate();
  const modalRef = useRef(null);

  const [loginTab, setLoginTab]           = useState('verein');
  const [formData, setFormData]           = useState({ email: '', passwort: '' });
  const [message, setMessage]             = useState('');
  const [loading, setLoading]             = useState(false);
  const [showPassword, setShowPassword]   = useState(false);
  const [athleteForm, setAthleteForm]     = useState({ email: '', passwort: '' });
  const [athleteMsg, setAthleteMsg]       = useState('');
  const [athleteLoading, setAthleteLoading] = useState(false);
  // Zweiter Faktor: gesetzt, sobald das Passwort stimmt und 2FA aktiv ist
  const [zwischenToken, setZwischenToken] = useState(null);
  const [code, setCode]                   = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMessage(''); setAthleteMsg(''); setLoginTab('verein');
    setZwischenToken(null); setCode('');
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Gemeinsamer Erfolgspfad — egal ob mit oder ohne zweiten Faktor.
  // Ablage seit 02.10.2026 in lib/anmeldungSpeichern.js (auch fürs gemeinsame Fenster).
  const uebernehmeAnmeldung = (data) => {
    const userData = vereinsAnmeldungSpeichern(data, formData.email);
    onClose();
    if (userData.user.role === 'admin') {
      if (onLogin) onLogin(userData); else navigate('/dashboard');
    } else {
      if (selectedItem?._typ === 'turnier') navigate(`/registration?turnierId=${selectedItem._id}`);
      else if (selectedItem?._typ === 'event') navigate(`/event-anmeldung/${selectedItem._id}`);
      // Ohne Rueckruf selbst weiterleiten -- ein Verein gehoert nach /verein,
      // nicht ins Turnier-Dashboard.
      else { if (onLogin) onLogin(userData); else navigate('/verein'); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setMessage('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Zweiter Faktor aktiv: hier gibt es noch kein Zugangstoken.
        if (data.zwei_faktor) {
          setZwischenToken(data.zwischen_token);
          setCode('');
          setMessage('');
        } else {
          uebernehmeAnmeldung(data);
        }
      } else {
        setMessage(data.error || data.message || 'Login fehlgeschlagen.');
      }
    } catch {
      setMessage('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2: Code aus der Authenticator-App (oder ein Wiederherstellungs-Code)
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setMessage('');
    try {
      const res = await fetch('/api/auth/2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zwischen_token: zwischenToken, code: code.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.wiederherstellungs_codes_offen !== null && data.wiederherstellungs_codes_offen !== undefined) {
          console.info(`Wiederherstellungs-Code benutzt — noch ${data.wiederherstellungs_codes_offen} übrig.`);
        }
        uebernehmeAnmeldung(data);
      } else {
        setMessage(data.error || 'Code stimmt nicht.');
        // Abgelaufenes Zwischen-Token: zurück zur Passwort-Eingabe
        if (res.status === 401 && /abgelaufen|Ungültige Anmeldung/i.test(data.error || '')) {
          setZwischenToken(null);
        }
      }
    } catch {
      setMessage('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleAthleteLogin = async (e) => {
    e.preventDefault();
    setAthleteLoading(true); setAthleteMsg('');
    try {
      const res = await fetch('/api/athlete/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: athleteForm.email, passwort: athleteForm.passwort }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('athleteToken', data.token);
        localStorage.setItem('athleteData', JSON.stringify(data.athlete));
        onClose();
        navigate('/athlete/dashboard');
      } else {
        setAthleteMsg(data.error || 'Login fehlgeschlagen.');
      }
    } catch {
      setAthleteMsg('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setAthleteLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ zIndex: 9999 }}
    >
      <div className="modal-box" ref={modalRef}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="modal-logo">
          <img src="/logo.webp" alt="TDA Tournament Software" />
        </div>
        <div className="modal-title">
          <h2>Login</h2>
          {selectedItem && loginTab === 'verein' ? (
            <p className="modal-selected-hint"><span className="modal-selected-dot" />{selectedItem.name}</p>
          ) : (
            <p>Willkommen zurück</p>
          )}
        </div>

        {/* Tab-Umschalter */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '1.25rem' }}>
          {[
            { id: 'verein', label: '🏢 Vereins-Login' },
            { id: 'athlet', label: '🥋 Athleten-Login' },
          ].map(t => (
            <button key={t.id} type="button"
              onClick={() => { setLoginTab(t.id); setMessage(''); setAthleteMsg(''); }}
              style={{
                flex: 1, padding: '0.6rem', background: 'none', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600,
                color: loginTab === t.id ? '#dc2626' : '#64748b',
                borderBottom: loginTab === t.id ? '2px solid #dc2626' : '2px solid transparent',
                transition: 'color 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Vereins-Login: Schritt 2 — Code aus der Authenticator-App */}
        {loginTab === 'verein' && zwischenToken && (
          <>
            {message && <div className="login-message error">{message}</div>}
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem', lineHeight: 1.5 }}>
              Dein Konto ist mit einem zweiten Faktor geschützt. Bitte den
              sechsstelligen Code aus deiner Authenticator-App eingeben.
            </p>
            <form onSubmit={handleCodeSubmit} className="login-form">
              <div className="input-group">
                <label>Code</label>
                <input
                  type="text" value={code} autoFocus required disabled={loading}
                  onChange={e => setCode(e.target.value)}
                  placeholder="123456"
                  inputMode="text" autoComplete="one-time-code"
                  style={{ letterSpacing: '0.15em', fontSize: '1.1rem' }}
                />
              </div>
              <button type="submit" className="login-button" disabled={loading}>
                {loading && <span className="login-spinner" />}
                {loading ? 'Prüfe Code...' : 'Bestätigen'}
              </button>
            </form>
            <div className="modal-footer" style={{ flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                Handy nicht zur Hand? Einer deiner Wiederherstellungs-Codes
                funktioniert an derselben Stelle.
              </span>
              <button onClick={() => { setZwischenToken(null); setCode(''); setMessage(''); }} className="register-link">
                Zurück zur Anmeldung
              </button>
            </div>
          </>
        )}

        {/* Vereins-Login: Schritt 1 */}
        {loginTab === 'verein' && !zwischenToken && (
          <>
            {message && (
              <div className={`login-message ${message.includes('erfolgreich') ? 'success' : 'error'}`}>{message}</div>
            )}
            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <label>Email oder Benutzername</label>
                <input type="text" name="email" value={formData.email}
                  onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                  placeholder="ihre@email.de" required disabled={loading} autoFocus />
              </div>
              <div className="input-group">
                <label>Passwort</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} name="passwort" value={formData.passwort}
                    onChange={e => setFormData(f => ({ ...f, passwort: e.target.value }))}
                    placeholder="Ihr Passwort" required disabled={loading}
                    style={{ paddingRight: '2.5rem', width: '100%', boxSizing: 'border-box' }} />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0', lineHeight: 1, fontSize: '1.1rem', color: 'var(--text-muted, #888)' }}
                    tabIndex={-1} aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}>
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <button type="submit" className="login-button" disabled={loading}>
                {loading && <span className="login-spinner" />}
                {loading ? 'Anmeldung läuft...' : 'Einloggen'}
              </button>
            </form>
            {/* Ohne diesen Weg war die Seite "Passwort vergessen" nicht
                erreichbar -- sie lag fertig im Repo, aber ohne Link. */}
            <div className="modal-footer">
              <button onClick={() => { onClose(); navigate('/forgot-password'); }} className="register-link">
                Passwort vergessen?
              </button>
            </div>
            <div className="modal-footer">
              <span>Noch kein Konto?</span>
              <button onClick={() => { onClose(); navigate('/register'); }} className="register-link">
                Verein registrieren
              </button>
            </div>
            {/* Aufgabe 132: Turniertag-Konten melden sich im gemeinsamen Fenster an */}
            <div className="modal-footer">
              <span>Kampfrichter, Personal, Waage?</span>
              <button onClick={() => { onClose(); navigate('/anmelden'); }} className="register-link">
                Zur Anmeldung
              </button>
            </div>
          </>
        )}

        {/* Athleten-Login */}
        {loginTab === 'athlet' && (
          <>
            {athleteMsg && (
              <div className="login-message error">{athleteMsg}</div>
            )}
            <form onSubmit={handleAthleteLogin} className="login-form">
              <div className="input-group">
                <label>E-Mail</label>
                <input type="email" value={athleteForm.email} required autoFocus
                  onChange={e => setAthleteForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="deine@email.de" disabled={athleteLoading} />
              </div>
              <div className="input-group">
                <label>Passwort</label>
                <input type="password" value={athleteForm.passwort} required
                  onChange={e => setAthleteForm(f => ({ ...f, passwort: e.target.value }))}
                  placeholder="Dein Passwort" disabled={athleteLoading} />
              </div>
              <button type="submit" className="login-button" disabled={athleteLoading}>
                {athleteLoading && <span className="login-spinner" />}
                {athleteLoading ? 'Anmeldung läuft...' : 'Als Athlet einloggen'}
              </button>
            </form>
            <div className="modal-footer">
              <span>Noch kein Konto?</span>
              <button onClick={() => { onClose(); navigate('/registrieren'); }} className="register-link">
                Jetzt registrieren
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
